import { useEffect } from "react";
import { create } from "zustand";
import { persist } from "zustand/middleware";

/**
 * Accessibility preferences — mirrored onto `<html data-*>` so CSS
 * variants in `tokens.css` can swap palettes/sizes without re-renders.
 *
 *   data-cv          color-vision palette       normal | protan | deuteran | tritan | mono
 *   data-contrast    contrast stance            normal | high
 *   data-font-scale  body / token scale         sm | md | lg | xl
 *   data-motion      motion override            on | off
 *   data-sr          screen-reader optimisations on | off    (toast → live region etc.)
 *
 * Why `data-*` instead of class chains? Tokens are defined as cascading
 * CSS variables; flipping a single attribute triggers an instant repaint
 * with zero JavaScript work.
 */

export type ColorVision = "normal" | "protan" | "deuteran" | "tritan" | "mono";
export type Contrast    = "normal" | "high";
export type FontScale   = "sm" | "md" | "lg" | "xl";
export type Motion      = "on" | "off";
export type SR          = "on" | "off";

/** Numeric multipliers consumed by AppShell when scaling the main area. */
export const FONT_SCALE_VALUES: Record<FontScale, number> = {
  sm: 0.9,
  md: 1.0,
  lg: 1.1,
  xl: 1.25,
};

interface A11yState {
  /**
   * 접근성 보조 기능을 쓸지 여부. **기본은 꺼짐**.
   *
   * 색각 팔레트·고대비·글자 확대는 필요한 사람에게는 필수지만, 그렇지 않은
   * 대다수에게는 제품의 주요 기능이 아니다. 설정 화면 앞자리에 펼쳐 두면
   * "이 시스템은 왜 이런 게 먼저 나오지?" 가 된다. 그래서 스위치 하나로
   * 접어 두고, 켠 사람에게만 항목이 열린다.
   *
   * 꺼져 있으면 저장된 값이 무엇이든 **표준(일반) 표시**로 렌더한다 — 예전에
   * 켜 뒀던 팔레트가 남아 다음 사람에게 그대로 보이는 일이 없도록.
   */
  enabled: boolean;
  colorVision: ColorVision;
  contrast: Contrast;
  fontScale: FontScale;
  motion: Motion;
  sr: SR;
  set: <K extends keyof A11ySetters>(key: K, value: A11ySetters[K]) => void;
  setEnabled: (on: boolean) => void;
  reset: () => void;
}

interface A11ySetters {
  colorVision: ColorVision;
  contrast: Contrast;
  fontScale: FontScale;
  motion: Motion;
  sr: SR;
}

const DEFAULTS: A11ySetters = {
  colorVision: "normal",
  contrast: "normal",
  fontScale: "md",
  motion: "on",
  sr: "off",
};

export const useA11y = create<A11yState>()(
  persist(
    (set) => ({
      enabled: false,
      ...DEFAULTS,
      set: (key, value) => set({ [key]: value } as Partial<A11yState>),
      // 끌 때 값까지 초기화한다. 끄기만 하고 값을 남겨 두면, 다시 켠 순간
      // 예전 설정이 되살아나 "왜 화면이 흑백이지?" 를 다시 겪는다.
      setEnabled: (on) => set(on ? { enabled: true } : { enabled: false, ...DEFAULTS }),
      reset: () => set({ ...DEFAULTS }),
    }),
    {
      name: "expertgas.a11y.v2",
      version: 2,
    },
  ),
);

/**
 * 현재 **실제로 적용될** 값. 접근성이 꺼져 있으면 저장값을 무시하고 표준값.
 * 화면을 그리는 쪽은 전부 이걸 쓴다.
 */
export function useEffectiveA11y(): A11ySetters & { enabled: boolean } {
  const s = useA11y();
  if (!s.enabled) return { enabled: false, ...DEFAULTS };
  return {
    enabled: true,
    colorVision: s.colorVision,
    contrast: s.contrast,
    fontScale: s.fontScale,
    motion: s.motion,
    sr: s.sr,
  };
}

/**
 * 색각 팔레트만 필요한 곳(3-D 트윈 등)이 쓰는 셀렉터.
 * 접근성이 꺼져 있으면 항상 "normal" — 저장값이 새어 나가지 않는다.
 */
export function useColorVision(): ColorVision {
  return useA11y((s) => (s.enabled ? s.colorVision : "normal"));
}

/** Mount once near the app root — mirrors store onto <html>. */
export function useBindA11y(): void {
  const { colorVision, contrast, fontScale, motion, sr } = useEffectiveA11y();

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.cv         = colorVision;
    root.dataset.contrast   = contrast;
    root.dataset.fontScale  = fontScale;
    root.dataset.motion     = motion;
    root.dataset.sr         = sr;
  }, [colorVision, contrast, fontScale, motion, sr]);
}
